"use client";

import { attachPreviewUrl } from "@/lib/upload/attach-preview-url";
import { AttachPreviewImage } from "@/components/admin/attach-picker/attach-preview-image";

import {
  FileArchiveIcon,
  FileAudioIcon,
  FileCodeIcon,
  FileIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  FileVideoIcon,
  ImageUpIcon,
  PresentationIcon,
  XIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";

import { AttachUploadRing } from "@/components/admin/attach-picker/attach-upload-ring";
import type { AttachUploadTaskStatus } from "@/lib/upload/attach-upload-client";
import { cn } from "@/lib/utils";
import { HoverTip } from "@/components/ui/tooltip";

function AttachTileFileIcon({
  mimeType,
  className,
}: {
  mimeType: string | null;
  className?: string;
}) {
  const iconClass = cn("text-muted-foreground", className);

  if (mimeType?.startsWith("video/")) {
    return <FileVideoIcon className={iconClass} />;
  }

  if (mimeType?.startsWith("audio/")) {
    return <FileAudioIcon className={iconClass} />;
  }

  if (mimeType?.includes("pdf") || mimeType?.includes("word")) {
    return <FileTextIcon className={iconClass} />;
  }

  if (
    mimeType?.includes("sheet") ||
    mimeType?.includes("excel") ||
    mimeType?.includes("csv")
  ) {
    return <FileSpreadsheetIcon className={iconClass} />;
  }

  if (mimeType?.includes("presentation") || mimeType?.includes("powerpoint")) {
    return <PresentationIcon className={iconClass} />;
  }

  if (
    mimeType?.includes("zip") ||
    mimeType?.includes("rar") ||
    mimeType?.includes("7z")
  ) {
    return <FileArchiveIcon className={iconClass} />;
  }

  if (mimeType?.startsWith("text/") || mimeType?.includes("javascript")) {
    return <FileCodeIcon className={iconClass} />;
  }

  return <FileIcon className={iconClass} />;
}

function isImageMime(mimeType: string | null) {
  return Boolean(mimeType?.startsWith("image/"));
}

export function AttachUploadTile({
  previewUrl,
  mimeType,
  progress = 0,
  phase,
  status = "success",
  error,
  empty = false,
  onRemove,
  onClick,
  onPickFile,
  className,
  iconSize = "md",
  showDelete = true,
  ariaLabel,
}: {
  previewUrl?: string;
  mimeType?: string | null;
  progress?: number;
  phase?: "uploading" | "saving";
  status?: AttachUploadTaskStatus | "idle";
  error?: string;
  empty?: boolean;
  onRemove?: () => void;
  onClick?: () => void;
  onPickFile?: () => void;
  className?: string;
  iconSize?: "sm" | "md";
  showDelete?: boolean;
  ariaLabel?: string;
}) {
  const t = useTranslations("site.attach");
  const isUploading = status === "uploading";
  const isError = status === "error";
  const showImage = Boolean(previewUrl && isImageMime(mimeType ?? null));
  const canDelete = showDelete && onRemove && (isUploading || isError || previewUrl);
  const iconClassName = iconSize === "sm" ? "size-3.5" : "size-10";

  return (
    <div
      className={cn(
        "group/attach-tile relative aspect-square w-full overflow-hidden rounded-lg border bg-background transition-colors",
        isError && "border-destructive ring-2 ring-destructive/20",
        empty && "border-dashed",
        className,
      )}
    >
      {empty ? (
        <button
          type="button"
          className="flex size-full cursor-pointer items-center justify-center text-muted-foreground hover:text-primary"
          onClick={onClick ?? onPickFile}
          aria-label={ariaLabel ?? t("chooseFile")}
        >
          <ImageUpIcon className={iconClassName} />
        </button>
      ) : (
        <>
          <div className="flex size-full items-center justify-center overflow-hidden">
            {showImage ? (
              <AttachPreviewImage src={attachPreviewUrl(previewUrl!, typeof window === "undefined" ? undefined : window.location.origin)} fallback={previewUrl} />
            ) : (
              <AttachTileFileIcon mimeType={mimeType ?? null} className={iconClassName} />
            )}
          </div>

          {isUploading || isError ? (
            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center bg-black/55",
                isError && "bg-destructive/15",
              )}
            >
              {isUploading ? (
                <div className="flex flex-col items-center gap-1"><AttachUploadRing progress={progress} />{phase === "saving" && <span className="text-[10px] text-white">{t("savingUpload")}</span>}</div>
              ) : (
                <span className="px-2 text-center text-[11px] font-medium text-destructive">
                  {t("uploadFailed")}
                </span>
              )}
            </div>
          ) : null}

          {onClick ? (
            <button
              type="button"
              className="absolute inset-0 z-0 cursor-pointer opacity-0"
              onClick={onClick}
              aria-label={ariaLabel ?? t("replaceFile")}
            />
          ) : null}

          {canDelete ? (
            <HoverTip content={error ?? (isUploading ? t("cancelUpload") : t("delete"))}><button
              type="button"
              className={cn(
                "absolute top-1.5 right-1.5 z-20 hidden cursor-pointer items-center justify-center rounded-full bg-black/55 p-1 text-white group-hover/attach-tile:flex",
                iconSize === "sm" ? "size-3.5" : "size-5",
              )}
              onClick={(event) => {
                event.stopPropagation();
                onRemove?.();
              }}
              aria-label={isUploading ? t("cancelUpload") : t("delete")}
            >
              <XIcon className={iconSize === "sm" ? "size-2" : "size-3"} />
            </button></HoverTip>
          ) : null}
        </>
      )}
    </div>
  );
}
